import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import inventoryApi from '../../../services/inventoryApi';
import '../inventory.css';

const PurchaseOrderItems = ({ purchaseOrderId, organization, onClose }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tableColumns, setTableColumns] = useState([]);
  const [columnSettings, setColumnSettings] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showColumnMenu, setShowColumnMenu] = useState(null);
  const [editFormData, setEditFormData] = useState({});

  // Add state for dropdown options
  const [products, setProducts] = useState([]);

  useEffect(() => {
    if (purchaseOrderId) {
      const loadData = async () => {
        loadColumnSettings();
        await loadItems();
        await loadDropdownData();
      };
      loadData();
    }
  }, [purchaseOrderId]);

  // Load column settings from localStorage
  const loadColumnSettings = () => {
    const saved = localStorage.getItem(`poItemColumns_${organization}`);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setColumnSettings(parsed.settings || {});
        setSortConfig(parsed.sort || { key: null, direction: 'asc' });
        return parsed.settings || {};
      } catch (e) {
        console.error('Error loading column settings:', e);
        return {};
      }
    }
    return {};
  };

  // Save column settings to localStorage
  const saveColumnSettings = (settings, sort) => {
    localStorage.setItem(`poItemColumns_${organization}`, JSON.stringify({
      settings,
      sort
    }));
  };

  // Load dropdown data for selects
  const loadDropdownData = async () => {
    try {
      const productsResponse = await inventoryApi.getProducts(organization);
      setProducts(Array.isArray(productsResponse) ? productsResponse : (productsResponse.data || []));
    } catch (err) {
      console.error('Error loading dropdown data:', err);
    }
  };

  const loadItems = async () => {
    if (!purchaseOrderId) {
      setError('Purchase Order ID is required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await inventoryApi.getPurchaseOrderItems(purchaseOrderId);
      const itemsData = Array.isArray(response) ? response : (response.data || []);
      setItems(itemsData);

      // Generate dynamic columns based on the first item
      if (itemsData.length > 0) {
        const saved = localStorage.getItem(`poItemColumns_${organization}`);
        let savedSettings = {};
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            savedSettings = parsed.settings || {};
          } catch (e) {
            console.error('Error parsing saved settings:', e);
          }
        }

        const firstItem = itemsData[0];
        const columns = Object.keys(firstItem)
          .filter(key => key !== 'id' && key !== 'purchaseOrderId')
          .map((key, index) => ({
            key,
            label: formatColumnLabel(key),
            visible: savedSettings[key]?.visible !== false,
            order: savedSettings[key]?.order ?? index
          }));
        columns.sort((a, b) => a.order - b.order);
        setTableColumns(columns);
      }
    } catch (err) {
      console.error('Error loading purchase order items:', err);
      setError(err.message || 'Failed to load purchase order items');
    } finally {
      setLoading(false);
    }
  };

  // Toggle column visibility
  const toggleColumnVisibility = (key) => {
    const newSettings = {
      ...columnSettings,
      [key]: {
        ...columnSettings[key],
        visible: !(columnSettings[key]?.visible ?? true)
      }
    };
    setColumnSettings(newSettings);
    saveColumnSettings(newSettings, sortConfig);

    setTableColumns(prev => prev.map(col =>
      col.key === key ? { ...col, visible: !col.visible } : col
    ));
  };

  // Move column up in order
  const moveColumnUp = (key) => {
    const currentIndex = tableColumns.findIndex(col => col.key === key);
    if (currentIndex <= 0) return;

    const newColumns = [...tableColumns];
    [newColumns[currentIndex - 1], newColumns[currentIndex]] =
      [newColumns[currentIndex], newColumns[currentIndex - 1]];

    const newSettings = { ...columnSettings };
    newColumns.forEach((col, index) => {
      newSettings[col.key] = { ...newSettings[col.key], order: index };
    });

    setColumnSettings(newSettings);
    saveColumnSettings(newSettings, sortConfig);
    setTableColumns(newColumns);
  };

  // Move column down in order
  const moveColumnDown = (key) => {
    const currentIndex = tableColumns.findIndex(col => col.key === key);
    if (currentIndex >= tableColumns.length - 1) return;

    const newColumns = [...tableColumns];
    [newColumns[currentIndex], newColumns[currentIndex + 1]] =
      [newColumns[currentIndex + 1], newColumns[currentIndex]];

    const newSettings = { ...columnSettings };
    newColumns.forEach((col, index) => {
      newSettings[col.key] = { ...newSettings[col.key], order: index };
    });

    setColumnSettings(newSettings);
    saveColumnSettings(newSettings, sortConfig);
    setTableColumns(newColumns);
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    const newSortConfig = { key, direction };
    setSortConfig(newSortConfig);
    saveColumnSettings(columnSettings, newSortConfig);
  };

  // Sort items
  const sortedItems = React.useMemo(() => {
    let sortableItems = [...items];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];

        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;

        if (aVal < bVal) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [items, sortConfig]);

  const filteredItems = sortedItems.filter(item => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return Object.values(item).some(value =>
      value?.toString().toLowerCase().includes(term)
    );
  });

  // Get visible columns
  const visibleColumns = tableColumns.filter(col => col.visible);

  // Reset column settings
  const resetColumnSettings = () => {
    localStorage.removeItem(`poItemColumns_${organization}`);
    setColumnSettings({});
    setSortConfig({ key: null, direction: 'asc' });
    loadItems();
  };

  // Helper function to format column labels
  const formatColumnLabel = (key) => {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
  };

  // Helper function to format cell values
  const formatCellValue = (value, key) => {
    if (value === null || value === undefined) return '-';

    // Format boolean values
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    // Format price/cost/total fields
    if ((key.includes('price') || key.includes('cost') || key.includes('total') || key.includes('amount')) && typeof value === 'number') {
      return `$${value.toFixed(2)}`;
    }

    // Format dates
    if (key.includes('date') || key.includes('Date') || key.includes('created') || key.includes('updated')) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString();
        }
      } catch (e) {
        return value;
      }
    }

    return value.toString();
  };

  // Helper function to determine if field should be a select
  const isSelectField = (key) => {
    return key.endsWith('Id') || key === 'productId';
  };

  // Helper function to get select options
  const getSelectOptions = (key) => {
    if (key === 'productId') {
      return products.map(prod => ({
        value: prod.id,
        label: `${prod.name || prod.productName} (${prod.sku || prod.code || ''})`
      }));
    }
    return [];
  };

  const handleOpenModal = (item = null) => {
    if (item) {
      setEditingId(item.id);
      setEditFormData({ ...item });
    } else {
      setEditingId(null);
      const defaults = {
        purchaseOrderId: purchaseOrderId,
        quantity: 1,
        unitPrice: 0
      };
      tableColumns.forEach(col => {
        if (!(col.key in defaults)) {
          defaults[col.key] = '';
        }
      });
      setEditFormData(defaults);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      await inventoryApi.deletePurchaseOrderItem(purchaseOrderId, itemId);
      await loadItems();
    } catch (err) {
      alert('Error deleting item: ' + err.message);
    }
  };

  const handleSave = async () => {
    try {
      const itemData = {
        ...editFormData,
        purchaseOrderId,
        // Ensure numeric fields are parsed
        quantity: parseFloat(editFormData.quantity) || 0,
        unitPrice: parseFloat(editFormData.unitPrice) || 0,
        total: (parseFloat(editFormData.quantity) || 0) * (parseFloat(editFormData.unitPrice) || 0)
      };

      if (editingId) {
        await inventoryApi.updatePurchaseOrderItem(purchaseOrderId, editingId, itemData);
      } else {
        await inventoryApi.createPurchaseOrderItem(purchaseOrderId, itemData);
      }

      await loadItems();
      handleCloseModal();
    } catch (err) {
      alert('Error saving item: ' + err.message);
    }
  };

  // Calculate totals
  const totals = React.useMemo(() => {
    return items.reduce((acc, item) => {
      const quantity = parseFloat(item.quantity) || 0;
      const unitPrice = parseFloat(item.unitPrice) || 0;
      const itemTotal = quantity * unitPrice;

      return {
        totalQuantity: acc.totalQuantity + quantity,
        totalAmount: acc.totalAmount + itemTotal
      };
    }, { totalQuantity: 0, totalAmount: 0 });
  }, [items]);

  if (loading) {
    return (
      <div className="pm-modal-overlay">
        <div className="pm-modal-content wide">
          <div className="pm-modal-header">
            <h2>{t('inventory.purchase_order_items_title')}</h2>
            <button className="pm-modal-close" onClick={onClose}>&times;</button>
          </div>
          <div className="pm-modal-body pm-skeleton" aria-busy="true">
            {t('inventory.loading_items_message')}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pm-modal-overlay">
        <div className="pm-modal-content wide">
          <div className="pm-modal-header">
            <h2>{t('inventory.purchase_order_items_title')}</h2>
            <button className="pm-modal-close" onClick={onClose}>&times;</button>
          </div>
          <div className="pm-modal-body">
            <p style={{ color: 'var(--pico-del-color)' }}>
              {t('inventory.error_prefix')} {error}
            </p>
            <button className="pm-btn-primary" onClick={loadItems}>
              {t('inventory.retry_button')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pm-modal-overlay" onClick={onClose}>
      <div className="pm-modal-content wide" onClick={(e) => e.stopPropagation()}>
        <div className="pm-modal-header">
          <h2>{t('inventory.purchase_order_items_title')}</h2>
          <div className="pm-modal-header-actions">
            <button className="pm-btn-primary" onClick={() => handleOpenModal()}>
              <span aria-hidden="true">➕</span>
              <span>{t('inventory.add_item_button')}</span>
            </button>
            <button className="pm-modal-close" onClick={onClose}>&times;</button>
          </div>
        </div>

        <div className="pm-modal-body">
          <div className="pm-toolbar">
            <div className="pm-search-wrap">
              <input
                type="search"
                placeholder={t('inventory.search_items')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {items.length === 0 ? (
            <div className="pm-empty-state">
              <div className="pm-empty-state-icon" aria-hidden="true">📋</div>
              <h3>{t('inventory.no_items_message')}</h3>
              <button className="pm-btn-primary" onClick={() => handleOpenModal()}>
                <span aria-hidden="true">➕</span>
                {t('inventory.add_item_button')}
              </button>
            </div>
          ) : (
            <div className="pm-table-wrap">
              <table className="pm-table" role="grid">
                <thead>
                  <tr>
                    <th style={{ width: '80px' }}>
                      <div className="pm-header-content">
                        {tableColumns.filter(c => !c.visible).length > 0 && (
                          <button
                            className="pm-column-btn visibility-btn"
                            onClick={() => setShowColumnMenu(showColumnMenu === 'hidden' ? null : 'hidden')}
                            title={t('inventory.show_hidden_columns_title')}
                          >
                            ⊕
                          </button>
                        )}
                        <div className="pm-header-label">{t('inventory.actions_header')}</div>
                      </div>
                      {showColumnMenu === 'hidden' && (
                        <div className="pm-column-menu">
                          <div className="menu-header">{t('inventory.hidden_columns_menu_header')}</div>
                          {tableColumns.filter(c => !c.visible).map(col => (
                            <button key={col.key} onClick={() => toggleColumnVisibility(col.key)}>
                              {t('inventory.show_column_button')} {col.label}
                            </button>
                          ))}
                          <hr />
                          <button onClick={() => {
                            resetColumnSettings();
                            setShowColumnMenu(null);
                          }}>
                            {t('inventory.reset_all_button')}
                          </button>
                        </div>
                      )}
                    </th>
                    {visibleColumns.map((col, index) => (
                      <th key={col.key} className="pm-sortable-header">
                        <div className="pm-header-content">
                          <div className="pm-header-controls">
                            <button
                              className="pm-column-btn"
                              onClick={() => moveColumnUp(col.key)}
                              disabled={index === 0}
                              title={t('inventory.move_left_title')}
                            >
                              ◀
                            </button>
                            <button
                              className="pm-column-btn"
                              onClick={() => moveColumnDown(col.key)}
                              disabled={index === visibleColumns.length - 1}
                              title={t('inventory.move_right_title')}
                            >
                              ▶
                            </button>
                            <button
                              className="pm-column-btn visibility-btn"
                              onClick={() => setShowColumnMenu(showColumnMenu === col.key ? null : col.key)}
                              title={t('inventory.column_options_title')}
                            >
                              ⋮
                            </button>
                          </div>
                          <div className="pm-header-label" onClick={() => handleSort(col.key)}>
                            {col.label}
                            {sortConfig.key === col.key && (
                              <span className="pm-sort-indicator">
                                {sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}
                              </span>
                            )}
                          </div>
                        </div>
                        {showColumnMenu === col.key && (
                          <div className="pm-column-menu">
                            <button onClick={() => {
                              toggleColumnVisibility(col.key);
                              setShowColumnMenu(null);
                            }}>
                              {t('inventory.hide_column_button')}
                            </button>
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <div className="pm-row-actions">
                          <button
                            className="pm-row-btn edit"
                            onClick={() => handleOpenModal(item)}
                            title={t('inventory.edit_tooltip')}
                          >
                            ✏️
                          </button>
                          <button
                            className="pm-row-btn delete"
                            onClick={() => handleDelete(item.id)}
                            title={t('inventory.delete_tooltip')}
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                      {visibleColumns.map(col => (
                        <td key={col.key}>
                          {formatCellValue(item[col.key], col.key)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={visibleColumns.length + 1}>
                      Total Quantity: {totals.totalQuantity.toFixed(2)} | Total Amount: ${totals.totalAmount.toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <div className="pm-modal-overlay" onClick={handleCloseModal}>
          <div className="pm-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="pm-modal-header">
              <h2>{editingId ? t('inventory.edit_item_title') : t('inventory.add_item_title')}</h2>
              <button className="pm-modal-close" onClick={handleCloseModal}>&times;</button>
            </div>
            <div className="pm-modal-body">
              <div className="pm-form-grid">
                {tableColumns.map(col => {
                  const value = editFormData[col.key] ?? '';
                  const isBoolean = typeof value === 'boolean';
                  const isNumber = col.key.includes('quantity') || col.key.includes('price') || col.key.includes('cost') || col.key.includes('total') || col.key.includes('amount');
                  const isTextarea = col.key === 'notes' || col.key === 'description';
                  const isSelect = isSelectField(col.key);
                  const selectOptions = isSelect ? getSelectOptions(col.key) : [];

                  if (isBoolean) {
                    return (
                      <div key={col.key} className="pm-form-group full-width">
                        <label>
                          <input
                            type="checkbox"
                            role="switch"
                            checked={!!value}
                            onChange={(e) => setEditFormData({ ...editFormData, [col.key]: e.target.checked })}
                          />
                          {col.label}
                        </label>
                      </div>
                    );
                  }

                  return (
                    <div key={col.key} className="pm-form-group">
                      <label>{col.label}</label>
                      {isSelect ? (
                        <select
                          value={value}
                          onChange={(e) => setEditFormData({ ...editFormData, [col.key]: e.target.value })}
                        >
                          <option value="">{t('inventory.select_placeholder')} {col.label} --</option>
                          {selectOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      ) : isTextarea ? (
                        <textarea
                          value={value}
                          onChange={(e) => setEditFormData({ ...editFormData, [col.key]: e.target.value })}
                        />
                      ) : (
                        <input
                          type={isNumber ? 'number' : col.key.includes('date') || col.key.includes('Date') ? 'date' : 'text'}
                          step={isNumber ? '0.01' : undefined}
                          value={value}
                          onChange={(e) => setEditFormData({ ...editFormData, [col.key]: e.target.value })}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="pm-modal-footer">
              <button className="pm-btn-secondary" onClick={handleCloseModal}>
                {t('inventory.cancel_button')}
              </button>
              <button className="pm-btn-primary" onClick={handleSave}>
                <span aria-hidden="true">💾</span>
                {editingId ? t('inventory.update_button') : t('inventory.create_button')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrderItems;
